import * as React from 'react';
import * as styles from '../components/styles.scss';
import classNames from 'classnames';
import { isFeatureEnabled } from 'owa-feature-flags';
import { AssetId, getAsset } from 'owa-timestream-store';
import { getAssetDescriptor, useProjectId } from 'owa-timestream-asset-sdk';
import { useMouseOrPrimaryPointerEvent } from 'owa-react-hooks/lib/useMouseOrPrimaryPointerEvent';
import {
    getAssetViewPositionStyle,
    getPanAndZoom,
    isAssetOverlapped,
    isMovingAssets,
    isSelectedAsset,
    pointerOverBucket,
    pointerOutBucket,
    getHoveringBucket,
    getSelectedAssetTypes,
} from '../viewstate';

export default function useTimeStreamAssetViewState(assetId: AssetId) {
    const projectId = useProjectId();
    const asset = getAsset(projectId, assetId);
    const { type } = asset;
    const assetDesc = getAssetDescriptor(type);
    const isSelected = isSelectedAsset(assetId);
    const positionStyle = getAssetViewPositionStyle(projectId, assetId, isSelected);
    const className = classNames(styles.assetView, {
        [styles.selectedAssetView]: isSelected,
        [styles.overlappedAssetView]: !isSelected && isAssetOverlapped(assetId),
        [styles.isMovingAssets]: isMovingAssets(),
        [styles.hoveringBucket]: getHoveringBucket() && !getSelectedAssetTypes().has('bucket'),
    });
    const title = isFeatureEnabled('cal-board-dev-showAssetInfo')
        ? JSON.stringify({ asset, positionStyle, panAndZoom: getPanAndZoom() }, null, 4)
        : undefined;

    const [onMouseOver, onPointerOver] = useMouseOrPrimaryPointerEvent(
        React.useCallback(
            (ev: React.MouseEvent<HTMLElement>) => {
                if (type === 'bucket') {
                    pointerOverBucket();
                }
            },
            [type]
        )
    );

    const [onMouseOut, onPointerOut] = useMouseOrPrimaryPointerEvent(
        React.useCallback(
            (ev: React.MouseEvent<HTMLElement>) => {
                if (type === 'bucket') {
                    pointerOutBucket();
                }
            },
            [type]
        )
    );

    return {
        assetAttributes: {
            title: title,
            className: className,
            style: positionStyle,
            onMouseOver: onMouseOver,
            onMouseOut: onMouseOut,
            onPointerOver: onPointerOver,
            onPointerOut: onPointerOut,
        } as React.HTMLAttributes<HTMLDivElement>,
        CanvasView: assetDesc?.CanvasView,
    };
}
